import { formatTimeTo12Hour } from "@/lib/utils";
import { parseYmdLocal } from "@/lib/utils/date/formatYmdLocal";

export type ClassCalendarSession = {
  date: Date;
  classId: number;
  className: string;
  time: string;
  startTime: string;
  location: string;
  studio: string;
  teacher?: string;
  currentStudents: number;
  maxStudents: number | null;
};

function clockValue(value: unknown): string {
  if (typeof value !== "string") return "";
  const match = value.match(/T(\d{2}:\d{2}(?::\d{2})?)/);
  if (!match) return "";
  return match[1].length === 5 ? `${match[1]}:00` : match[1];
}

function clockLabel(value: unknown): string {
  const clock = clockValue(value);
  return clock ? formatTimeTo12Hour(clock) : "";
}

function sessionDate(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  const ymd = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  return parseYmdLocal(ymd);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function mapClassCalendarItem(
  value: unknown,
): ClassCalendarSession | null {
  const row = asRecord(value);
  if (!row) return null;
  const date = sessionDate(row.date);
  const classId = Number(row.classId);
  const className =
    typeof row.className === "string" ? row.className.trim() : "";
  if (!date || !Number.isFinite(classId) || classId <= 0 || !className)
    return null;

  const startClock = clockValue(row.startTime);
  const startLabel = startClock ? formatTimeTo12Hour(startClock) : "";
  const endLabel = clockLabel(row.endTime);
  const teachers = Array.isArray(row.teacherNames)
    ? row.teacherNames
        .filter(
          (name): name is string =>
            typeof name === "string" && name.trim().length > 0,
        )
        .join(", ")
    : "";
  const maxRaw = row.maxNumberOfStudents;
  const maxStudents = maxRaw == null || maxRaw === "" ? null : Number(maxRaw);

  return {
    date,
    classId,
    className,
    time:
      startLabel && endLabel
        ? `${startLabel} – ${endLabel}`
        : startLabel || endLabel,
    startTime: startClock,
    location:
      typeof row.locationName === "string" && row.locationName.trim()
        ? row.locationName
        : "-",
    studio:
      typeof row.classRoomName === "string" && row.classRoomName.trim()
        ? row.classRoomName
        : "-",
    teacher: teachers || undefined,
    currentStudents: Number(row.enrollmentCount) || 0,
    maxStudents:
      maxStudents != null && Number.isFinite(maxStudents) ? maxStudents : null,
  };
}

export function readClassesCalendarSessions(
  items: Array<{ key?: string; data?: { $type?: string; items?: unknown } }>,
): ClassCalendarSession[] {
  const match = items.find(
    (item) =>
      item.key === "classes-calendar" ||
      item.data?.$type === "classes-calendar",
  );
  const rows = match?.data?.items;
  if (!Array.isArray(rows)) return [];
  return rows
    .map(mapClassCalendarItem)
    .filter((session): session is ClassCalendarSession => session != null);
}
